import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { CloudUpload, WarningAmber } from "@mui/icons-material";
import { useMutation, useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import {
  GET_MATCHING_GROUP_CATEGORIES,
  GetMatchingGroupCategoriesResult,
  PUSH_SELECTED_TEAMS_TO_LMS,
  PushSelectedTeamsToLmsResult,
} from "../../Queries";
import { Team } from "../../types";

interface Props {
  teams: Team[];
  /**
   * Group ids applied by the most recent matching run — used to split the
   * selection list into "latest matching run" vs "other teams" (manual or
   * from older runs). Either section is hidden when empty.
   */
  latestRunGroupIds: string[];
  onClose: () => void;
  onQueued: (message: string) => void;
}

/**
 * Dialog behind the Teams-page "Push Teams to LMS" action: push any selection
 * of existing teams to the LMS as a group set, with the same overwrite-vs-new
 * group-set choice as the matching finalize dialog. Works without a matching
 * run and doubles as the retry path when a run's automatic push failed.
 */
export function PushToLmsDialog({
  teams,
  latestRunGroupIds,
  onClose,
  onQueued,
}: Props) {
  const [t] = useTranslation();

  const [mode, setMode] = useState<"new" | "existing">("new");
  const [newGroupSetName, setNewGroupSetName] = useState(
    t("modals.pushTeamsToLms.defaultGroupSetName"),
  );
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null,
  );
  // Default selection: the most recent matching run's teams when one exists
  // (the common push/retry target), otherwise everything.
  const [selectedTeamIds, setSelectedTeamIds] = useState<Set<string>>(() => {
    const runSelected = teams.filter((team) =>
      latestRunGroupIds.includes(team.id),
    );
    const defaults = runSelected.length > 0 ? runSelected : teams;
    return new Set(defaults.map((team) => team.id));
  });
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data: categoriesData, loading: categoriesLoading } =
    useQuery<GetMatchingGroupCategoriesResult>(GET_MATCHING_GROUP_CATEGORIES);
  const categories = categoriesData?.matchingGroupCategories ?? [];

  const [pushTeams, { loading: pushing }] =
    useMutation<PushSelectedTeamsToLmsResult>(PUSH_SELECTED_TEAMS_TO_LMS, {
      onError: (error) => setErrorMsg(error.message),
      onCompleted: (data) => {
        const result = data.pushSelectedTeamsToLms;
        if ("ok" in result && result.ok) {
          onQueued(
            t("modals.pushTeamsToLms.queued", { count: selectedTeamIds.size }),
          );
        } else if ("messages" in result) {
          setErrorMsg(
            result.messages.map((m) => m.message).join(" ") ||
              t("modals.pushTeamsToLms.genericError"),
          );
        }
      },
    });

  const runIdSet = useMemo(
    () => new Set(latestRunGroupIds),
    [latestRunGroupIds],
  );
  const runTeams = teams.filter((team) => runIdSet.has(team.id));
  const otherTeams = teams.filter((team) => !runIdSet.has(team.id));
  // Section headers only make sense when there are two sources to tell apart.
  const showSections = runTeams.length > 0 && otherTeams.length > 0;

  function toggleTeam(id: string) {
    setSelectedTeamIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleAll() {
    setSelectedTeamIds((prev) =>
      prev.size === teams.length
        ? new Set<string>()
        : new Set(teams.map((team) => team.id)),
    );
  }

  async function handleConfirm() {
    setErrorMsg(null);
    await pushTeams({
      variables: {
        groupIds: [...selectedTeamIds],
        groupCategoryId: mode === "existing" ? selectedCategoryId : null,
        newGroupSetName: mode === "new" ? newGroupSetName : null,
      },
    });
  }

  const confirmDisabled =
    pushing ||
    selectedTeamIds.size === 0 ||
    (mode === "new" && !newGroupSetName.trim()) ||
    (mode === "existing" && !selectedCategoryId);

  // Teams that would leave their current group set if pushed to the chosen
  // target. A new-set name that matches an existing live set resolves to that
  // set (same rule as the backend), so staying put is not flagged.
  const targetChosen =
    mode === "new" ? newGroupSetName.trim().length > 0 : !!selectedCategoryId;
  const targetCategoryId =
    mode === "existing"
      ? selectedCategoryId
      : categories.find((cat) => cat.name === newGroupSetName.trim())?.id ??
        null;
  const reassignedTeams = targetChosen
    ? teams.filter(
        (team) =>
          selectedTeamIds.has(team.id) &&
          team.categoryId &&
          team.categoryId !== targetCategoryId,
      )
    : [];

  const renderTeamItems = (items: Team[]) =>
    items.map((team) => (
      <ListItemButton
        key={team.id}
        dense
        onClick={() => toggleTeam(team.id)}
        aria-label={`toggle team ${team.name}`}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Checkbox
            edge="start"
            size="small"
            checked={selectedTeamIds.has(team.id)}
            tabIndex={-1}
            disableRipple
          />
        </ListItemIcon>
        <ListItemText
          primary={team.name}
          secondary={t("modals.pushTeamsToLms.memberCount", {
            count: team.userIds.length,
          })}
        />
      </ListItemButton>
    ));

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="push-to-lms-dialog-title"
    >
      <DialogTitle id="push-to-lms-dialog-title">
        {t("modals.pushTeamsToLms.title")}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {t("modals.pushTeamsToLms.prompt")}
        </Typography>

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMsg}
          </Alert>
        )}

        <RadioGroup
          value={mode}
          onChange={(e) => setMode(e.target.value as "new" | "existing")}
        >
          <FormControlLabel
            value="new"
            control={<Radio />}
            label={t("modals.pushTeamsToLms.createNewGroupSet")}
          />

          {mode === "new" && (
            <TextField
              label={t("modals.pushTeamsToLms.groupSetName")}
              value={newGroupSetName}
              onChange={(e) => setNewGroupSetName(e.target.value)}
              size="small"
              fullWidth
              sx={{ ml: 4, mb: 1, width: "calc(100% - 32px)" }}
              inputProps={{
                "aria-label": t("modals.pushTeamsToLms.groupSetNameAria"),
              }}
            />
          )}

          <FormControlLabel
            value="existing"
            control={<Radio />}
            label={t("modals.pushTeamsToLms.replaceExistingGroupSet")}
          />

          {mode === "existing" && (
            <FormControl
              size="small"
              fullWidth
              sx={{ ml: 4, width: "calc(100% - 32px)" }}
            >
              <InputLabel id="push-category-select-label">
                {t("modals.pushTeamsToLms.groupSet")}
              </InputLabel>
              <Select
                labelId="push-category-select-label"
                label={t("modals.pushTeamsToLms.groupSet")}
                value={selectedCategoryId ?? ""}
                onChange={(e) =>
                  setSelectedCategoryId(e.target.value as string)
                }
                inputProps={{
                  "aria-label": t("modals.pushTeamsToLms.existingGroupSetAria"),
                }}
                disabled={categoriesLoading}
              >
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
                {categories.length === 0 && !categoriesLoading && (
                  <MenuItem disabled value="">
                    {t("modals.pushTeamsToLms.noGroupSets")}
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          )}
        </RadioGroup>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2">
          {t("modals.pushTeamsToLms.teamsHeading")}
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={selectedTeamIds.size === teams.length}
              indeterminate={
                selectedTeamIds.size > 0 && selectedTeamIds.size < teams.length
              }
              onChange={toggleAll}
              inputProps={{
                "aria-label": t("modals.pushTeamsToLms.selectAllAria"),
              }}
            />
          }
          label={t("modals.pushTeamsToLms.selectAll")}
        />
        <List dense disablePadding>
          {showSections && (
            <ListSubheader disableSticky disableGutters>
              {t("modals.pushTeamsToLms.latestRunSection")}
            </ListSubheader>
          )}
          {renderTeamItems(runTeams)}
          {showSections && (
            <ListSubheader disableSticky disableGutters>
              {t("modals.pushTeamsToLms.otherTeamsSection")}
            </ListSubheader>
          )}
          {renderTeamItems(otherTeams)}
        </List>

        {/* Deliberately NOT a MuiAlert: the global Alert override paints
            warnings as a solid --color-yellow flash block. This follows the
            app's quieter warning idiom instead (warning.main amber on dark
            surfaces — see MatchingPreviewModal's conflict lines). */}
        {reassignedTeams.length > 0 && (
          <Box
            role="alert"
            sx={{
              mt: 2,
              p: 1.5,
              display: "flex",
              gap: 1.5,
              borderRadius: 1,
              border: "1px solid",
              borderColor: "warning.main",
            }}
          >
            <WarningAmber fontSize="small" sx={{ color: "warning.main" }} />
            <Box>
              <Typography
                variant="body2"
                sx={{ color: "warning.main", fontWeight: 600 }}
              >
                {t("modals.pushTeamsToLms.reassignWarningTitle", {
                  count: reassignedTeams.length,
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("modals.pushTeamsToLms.reassignWarningBody")}
              </Typography>
              <List dense disablePadding sx={{ listStyle: "disc", pl: 2.5 }}>
                {reassignedTeams.map((team) => (
                  <ListItemText
                    key={team.id}
                    sx={{ display: "list-item", my: 0 }}
                    primaryTypographyProps={{ variant: "body2" }}
                    primary={t("modals.pushTeamsToLms.reassignWarningItem", {
                      name: team.name,
                      category: team.categoryName,
                    })}
                  />
                ))}
              </List>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("modals.pushTeamsToLms.cancel")}</Button>
        <Button
          variant="contained"
          startIcon={<CloudUpload />}
          onClick={handleConfirm}
          disabled={confirmDisabled}
          aria-label={t("modals.pushTeamsToLms.confirmAria")}
        >
          {pushing
            ? t("modals.pushTeamsToLms.pushing")
            : t("modals.pushTeamsToLms.push")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
